// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import { Flex, Paper, Table } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { InfoIcon } from 'components/Mantine/InfoIcon';
import { mapAmount } from 'helpers/mapAmount';

import type { RoleOption } from './types';

const PermissionIcon = ({ role }: { role: boolean }) =>
  role ? <EvolveIcon icon="Check" color="teal" /> : <EvolveIcon icon="Close" size="sm" color="gray" />;

export const PermissionGuideTable = <
  T extends {
    permissionLabel: string;
    permissionTooltip?: ReactNode;
  } & {
    [key in K]: RoleOption;
  },
  K extends Exclude<keyof T, 'permission'>,
>({
  title,
  roleNames,
  roleData,
  roleOrder,
  numColumns,
}: {
  title: string;
  roleNames: readonly string[];
  roleData: readonly T[];
  roleOrder: readonly K[];
  numColumns: number;
}) => (
  <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
    <Table>
      <Table.Thead bg="gray.1">
        <Table.Tr display="flex">
          <Table.Th w={300}>{title}</Table.Th>
          {roleNames.map((rn) => (
            <Table.Th key={rn} flex={1}>
              {rn}
            </Table.Th>
          ))}
          {mapAmount(numColumns - roleNames.length, (key) => (
            <Table.Th key={key} flex={1} />
          ))}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {roleData.map((r) => (
          <Table.Tr key={r.permissionLabel} display="flex">
            <Table.Td w={300}>
              <Flex align="center" gap="xs">
                {r.permissionLabel}
                <InfoIcon label={r.permissionTooltip} />
              </Flex>
            </Table.Td>
            {roleOrder.map((ro) => (
              <Table.Td key={String(ro)} flex={1} width="100%">
                <Flex justify="flex-start" align="center" direction="row" h="100%">
                  <PermissionIcon role={r[ro].assigned} />
                </Flex>
              </Table.Td>
            ))}
            {mapAmount(numColumns - roleOrder.length, (key) => (
              <Table.Td key={key} flex={1} />
            ))}
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  </Paper>
);
